import fetchWithAuth from './base';
import { ListResponse } from '@/types/common';
import type {
  ApiCreditPayment,
  ApiCreditPaymentQuote,
  ApiCreditPaymentSummary,
  ApiPaymentMethod,
  CreateCreditPaymentRequest,
  CreateStripeSessionRequest,
  CreateStripeSessionResponse,
  PaymentMethodSetupIntentResponse,
  StripeBillingUrlResponse,
} from '@/types/stripe';

export async function createStripeSession(
  payload: CreateStripeSessionRequest
): Promise<CreateStripeSessionResponse> {
  const clientId = payload.clientId;
  const response = await fetchWithAuth(`/api/client/${clientId}/stripe/session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      plan_id: payload.planId,
      checkout_theme: payload.checkoutTheme,
    }),
  });

  const data = (await response.json()) as Partial<CreateStripeSessionResponse> & {
    error?: string;
    message?: string;
  };

  if (!response.ok) {
    throw new Error(data.error || data.message || 'Unable to start checkout');
  }

  if (!data.client_secret) {
    throw new Error('Stripe checkout client secret is missing.');
  }

  return {
    client_secret: data.client_secret,
    ...(data.session_id ? { session_id: data.session_id } : {}),
  };
}

export async function getStripeBillingUrl(clientId: string): Promise<StripeBillingUrlResponse> {
  const response = await fetchWithAuth(`/api/client/${clientId}/stripe/billing`, {
    method: 'POST',
  });

  const data = (await response.json()) as Partial<StripeBillingUrlResponse> & {
    error?: string;
    message?: string;
  };

  if (!response.ok || !data.billing_url) {
    throw new Error(data.error || data.message || 'Unable to open billing info');
  }

  return {
    billing_url: data.billing_url,
  };
}

export async function getPaymentMethod(clientId: string): Promise<ApiPaymentMethod | null> {
  const response = await fetchWithAuth(`/api/client/${clientId}/payment_method`, {
    method: 'GET',
  });
  const paymentMethod = (await response.json()) as ApiPaymentMethod & {
    error?: string;
    message?: string;
  };
  if (!response.ok) {
    throw new Error(
      paymentMethod.error || paymentMethod.message || 'Unable to load payment method'
    );
  }
  return paymentMethod.id && paymentMethod.card_network && paymentMethod.card_last_4
    ? paymentMethod
    : null;
}

export async function createPaymentMethodSetupIntent(
  clientId: string
): Promise<PaymentMethodSetupIntentResponse> {
  const response = await fetchWithAuth(`/api/client/${clientId}/payment_method/setup_intent`, {
    method: 'POST',
  });
  const data = (await response.json()) as PaymentMethodSetupIntentResponse & {
    error?: string;
    message?: string;
  };
  if (!response.ok || !data.client_secret) {
    throw new Error(data.error || data.message || 'Unable to set up payment method');
  }
  return data;
}

export async function getCreditPayments(
  clientId: string,
  startingAfter?: string
): Promise<ListResponse<ApiCreditPayment>> {
  const params = startingAfter ? `?starting_after=${startingAfter}` : '';
  const response = await fetchWithAuth(`/api/client/${clientId}/credit_payment${params}`, {
    method: 'GET',
  });
  return response.json();
}

export async function getCreditPaymentSummary(
  clientId: string,
  startDate: string,
  endDate: string
): Promise<ApiCreditPaymentSummary> {
  const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
  const response = await fetchWithAuth(
    `/api/client/${clientId}/credit_payment/summary?${params.toString()}`,
    { method: 'GET' }
  );
  return response.json();
}

export async function getCreditPaymentQuote(
  clientId: string,
  amount: string
): Promise<ApiCreditPaymentQuote> {
  const response = await fetchWithAuth(
    `/api/client/${clientId}/credit_payment/quote?amount=${encodeURIComponent(amount)}`,
    { method: 'GET' }
  );
  const data = (await response.json()) as {
    amount?: string;
    total_credits?: number;
    credits?: number;
  };

  return {
    amount: data.amount ?? amount,
    total_credits: data.total_credits ?? data.credits ?? 0,
  };
}

export async function createCreditPayment({
  clientId,
  creditPackageId,
  amount,
}: CreateCreditPaymentRequest): Promise<ApiCreditPayment> {
  const response = await fetchWithAuth(`/api/client/${clientId}/credit_payment`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(creditPackageId ? { credit_package_id: creditPackageId } : { amount }),
  });
  const data = (await response.json()) as ApiCreditPayment & {
    error?: string;
    message?: string;
  };
  if (!response.ok) {
    throw new Error(data.error || data.message || 'Unable to add credits');
  }
  return data;
}
